import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Output, EventEmitter, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Subscription } from 'rxjs';
import { F1065SchKService } from './f1065-sch-k.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, generalConfigFromGlobal, normalizeFilterParams, rowPayloadFromActiveScreen } from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-f1065-sch-k',
    templateUrl: './f1065-sch-k.component.html',
    styleUrls: ['./f1065-sch-k.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065SchKComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  // ========== STANDARD COMPONENT PROPERTIES ==========
  globalService: any = {};
  rowData: any = {};
  filterParams: any = {};
  baseUrl: string = '';
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  processName: string = "Schedule K- Partners' Distributive Share Items";
  scenarioLabel: string = '';

  // ========== COMPONENT STATE ==========
  title: string = "Schedule K- Partners' Distributive Share Items";
  modalTitle: string = "Schedule K- Partners' Distributive Share Items";
  taxYear: number = 2024;
  showFormLoader: boolean = true;
  isSaving: boolean = false;
  canSave: boolean = true;
  isInitialized: boolean = false;
  isLoading: boolean = false;
  minimized: boolean = false;
  ISSUE_DESC: string = '';
  subtableString: string = 'Loading ...';

  // ========== DATA STRUCTURES ==========
  entityList: any = {};
  formRows: any[] = [];
  formRowsOriginal: any[] = [];
  optionsList: string[] = [];
  line20CNumber: number = -1;
  line20C1Number: number = -1;
  subtableRows: any[] = []; // Direct reference for subtable data
  subtableRowsOriginal: any[] = []; // Backup for reset functionality

  // Options lists by year
  private optionsList2022: string[] = [
    "Code C. Fuel tax credit information.",
    "Code D. Qualified rehabilitation expenditures (other than rental real estate).",
    "Code D1. Rehabilitation credit (Sec 47) - other than rental real estate",
    "Code E. Basis of energy property.",
    "Code F. Recap Low Inc Hous. Credit 42(J)(5)",
    "Code G. Recap Low-Inc Hous. Credit (Other)",
    "Code H. Recapture of investment credit.",
    "Code I. Recapture of other credits.",
    "Code J. Look-back interest—completed long-term contracts.",
    "Code K. Look-back interest—income forecast method.",
    "Code L. Dispositions of property with section 179 deductions.",
    "Code M. Recapture of section 179 deduction.",
    "Code N. Business interest expense",
    "Code O. Section 453(l)(3) information.",
    "Code P. Section 453A(c) information.",
    "Code Q. Section 1260(b) information.",
    "Code R. Interest allocable to production expenditures.",
    "Code S. Capital construction fund (CCF) nonqualified withdrawals.",
    "Code T. Depletion information—oil and gas.",
    "Code U. Section 743(b) basis adjustment.",
    "Code V. Unrelated business taxable income.",
    "Code W. Precontribution gain (loss).",
    "Code Y. Net investment income.",
    "Codes Z. Section 199A information.",
    "Code AA. Section 704(c) information",
    "Code AB. Section 751 gain (loss).",
    "Code AC. Section 1(h)(5) gain (loss).",
    "Code AD. Deemed section 1250 unrecaptured gain.",
    "Code AE. Excess taxable income.",
    "Code AF. Excess business interest income.",
    "Code AG. Gross receipts for section 448(c)",
    "Code AH. Other information. ",
    "20AH1. Section 743(b) negative adjustments - Goodwill Amortization",
    "20AH2. Section 743(b) negative adjustments - Lifo Adjustments",
    "Z1. Section 199A Qualified Business Income",
    "Z2. Section 199A W-2 Wages",
    "Z3. Section 199A Unadjusted Basis",
    "Z4. Section 199A REIT Dividends",
    "Z5. Section 199A PTP Income"
  ];

  private optionsList2023: string[] = [
    ...this.optionsList2022.slice(0, 21),
    "Code W. Precontribution gain (loss).",
    "Code X. Payment obligations including guarantees and deficit restoration obligations (DROs)",
    "Code Y. Net investment income.",
    "Codes Z. Section 199A information.",
    "Code AA. Section 704(c) information",
    "Code AB. Section 751 gain (loss).",
    "Code AC. Section 1(h)(5) gain (loss).",
    "Code AD. Deemed section 1250 unrecaptured gain.",
    "Code AE. Excess taxable income.",
    "Code AF. Excess business interest income.",
    "Code AG. Gross receipts for section 448(c)",
    "Code AH. Other information. ",
    "20AH1. Section 743(b) negative adjustments - Goodwill Amortization",
    "20AH2. Section 743(b) negative adjustments - Lifo Adjustments",
    "Z1. Section 199A Qualified Business Income",
    "Z2. Section 199A W-2 Wages",
    "Z3. Section 199A Unadjusted Basis",
    "Z4. Section 199A REIT Dividends",
    "Z5. Section 199A PTP Income",
    "Code AH3. Noncash charitable contributions",
    "Code AI. Interest and tax on deferred compensation to partners",
    "Code AJ. Excess business loss limitation",
    "Code AK. Gain from mark-to-market election",
    "Code AL. Section 721(c) partnership",
    "Code AM. Section 1061 information",
    "Code AN. Farming and fishing business",
    "Code AO. PTP information",
    "Code AP. Inversion gain",
    "Code AQ. Conservation reserve program payments",
    "Code AR. IRA disclosure",
    "Code AS. Qualifying advanced coal project property and qualifying gasification project property",
    "Code AT. Qualifying advanced energy project property",
    "Code AU. Advanced manufacturing investment property",
    "Code AV. Reserved for future usev",
    "Code AW. Reportable transactions",
    "Code AY. Foreign partners, Form 8990, Schedule A",
    "Code ZZ. Other"
  ];

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();
  private readonly subs = new Subscription();

  // ========== INPUT SETTERS WITH tryInit() ==========

  // CRITICAL: Subtable data loaded by AngularJS (has access to JsonObjectFactory with parent_me_string)
  @Input('subtable-data')
  set subtableDataInput(value: string) {
    try {
      if (value && value !== '[]' && value !== '') {
        const data = safeJsonInput<any>(value, null, 'f1065-sch-k.parse1');
        console.log('Subtable data received from AngularJS:', data);
        if (data && data.length > 0) {
          this.processSubtableDataFromAngularJS(data);
        }
      }
    } catch (e) {
      console.log('subtable-data not yet stable');
    }
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private f1065SchKService: F1065SchKService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseUrl = generalConfigFromGlobal(this.gtwGlobal)?.base_url || generalConfigFromGlobal(this.gtwGlobal)?.api || '';
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.processName = this.globalService.processName || "Schedule K- Partners' Distributive Share Items";
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.taxYear = parseInt(this.rowData?.TAX_YEAR, 10) || 2024;
    this.scenarioLabel = this.filterParams?.scenarioLabel || this.filterParams?.scenario_label || this.globalService?.scenarioLabel || '';

    if (this.filterParams?.parent_me_string) {
      this.globalService.globalParams.parent_me_string = this.filterParams.parent_me_string;
    }
  }

  // ========== INITIALIZATION GATE ==========
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.baseUrl || !this.globalService?.globalParams || !this.rowData?.TAX_YEAR) return;

    this.isLoading = true;
    this.init();
  }

  private init(): void {
    this.isInitialized = true;
    this.setupEntityData();
    this.setupOptionsList();
    this.loadData();
  }

  private setupEntityData(): void {
    this.entityList = {
      'Tax Year': this.rowData.TAX_YEAR,
      'LEID': this.rowData.LEID,
      'LE Name': this.rowData.LE_NAME
    };

    this.title = this.modalTitle + ' ( Tax Year : ' + this.rowData.TAX_YEAR + ' - Scenario : ' + this.scenarioLabel + ' )';
    this.ISSUE_DESC = this.rowData.ISSUE_DESC || '';
  }

  private setupOptionsList(): void {
    if (this.taxYear < 2023) {
      this.optionsList = [...this.optionsList2022];
    } else if (this.taxYear === 2023) {
      this.optionsList = [...this.optionsList2023];
    } else {
      // For 2024+, load from API
      this.loadOptionsFromApi();
    }
  }

  private loadOptionsFromApi(): void {
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    this.f1065SchKService.loadOptionsList(this.baseUrl, this.globalService?.globalParams || {}, this.rowData, ssoId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.jsonObject) {
            this.optionsList = Object.keys(response.jsonObject).map(key => response.jsonObject[key].SUB_ACCT);
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading options list:', err);
          this.optionsList = [...this.optionsList2023]; // Fallback
        }
      });
  }

  // ========== DATA LOADING ==========
  private loadData(): void {
    this.showFormLoader = true;

    forkJoin({
      formData: this.f1065SchKService.loadFormData(this.baseUrl, this.rowData, this.globalService?.globalParams || {}),
      subtable: this.f1065SchKService.loadSubtableRecords(this.baseUrl, this.rowData, this.globalService?.globalParams || {})
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (responses) => {
        this.processFormData(responses.formData);
        this.processSubtableData(responses.subtable);
        
        this.showFormLoader = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[SchK DEBUG] Error loading data:', err);
        this.showFormLoader = false;
        this._snackBar.open('Error loading form data. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    });
  }

  private processFormData(data: any): void {
    if (!data?.irsForm?.formItemList) {
      console.error('No form data received');
      return;
    }

    this.formRows = [];

    for (let i = 2; i < data.irsForm.formItemList.length; i++) {
      const formItem = data.irsForm.formItemList[i];
      const rowDimensions = formItem.dataRowList[0].rowDimensions.object_id.split('~');
      const dataCellList = formItem.dataRowList[0].dataCellList;

      const tempDataObj: any = {
        saveParams: rowDimensions,
        line_order: rowDimensions[8],
        JSON_DATA: []
      };

      for (let j = 0; j < dataCellList.length; j++) {
        const cell = dataCellList[j];

        if (j === 0) {
          tempDataObj.num = cell.name;
        } else if (j === 1) {
          tempDataObj.description = cell.name;
        } else if (j === 2) {
          const keyParts = cell.key.split('~');
          tempDataObj.lineAttrKeyA = keyParts[1];
          tempDataObj.attrib_name_a = keyParts[0];
          tempDataObj.attrib_order_a = keyParts[2];
          tempDataObj.is_editable = cell.property?.is_editable_cell !== 'Y';

          let value = cell.value;
          if (value === null || value === '') {
            if (['6a', '6b', '6c', '9a', '9b', '9c'].includes(tempDataObj.num)) {
              value = 0;
            } else {
              value = '';
            }
          }
          if (tempDataObj.num === '16p(1)' || tempDataObj.num === '16p(2)') {
            value = value === 'true';
          }
          tempDataObj.value1 = value;
        } else {
          const keyParts = cell.key.split('~');
          tempDataObj.lineAttrKeyB = keyParts[1];
          tempDataObj.attrib_name_b = keyParts[0];
          tempDataObj.attrib_order_b = keyParts[2];

          let value = cell.value;
          if (value === null || value === '') {
            if (['6a', '6b', '6c', '9a', '9b', '9c'].includes(tempDataObj.num)) {
              value = 0;
            } else {
              value = '';
            }
          }
          if (tempDataObj.num === '16p(1)' || tempDataObj.num === '16p(2)') {
            value = value === 'true';
          }
          tempDataObj.value2 = value;
        }
      }

      this.formRows.push(tempDataObj);

      // Track line 20c and 20c1 indices
      if (tempDataObj.num === '20c') {
        this.line20CNumber = this.formRows.length - 1;
      }
      if (tempDataObj.num === '20c1') {
        this.line20C1Number = this.formRows.length - 1;
      }
    }

    // Initialize JSON_DATA for line 20c
    if (this.line20CNumber >= 0) {
      this.formRows[this.line20CNumber].JSON_DATA = [];
    }

    this.formRowsOriginal = safeJsonInput<any>(JSON.stringify(this.formRows), null, 'f1065-sch-k.parse2');
  }

  private processSubtableData(data: any): void {

    if (!data?.jsonObject || data.jsonObject.length === 0) {
      console.log('No jsonObject data, setting No Records');
      this.subtableString = 'No Records.';
      return;
    }

    // Group by OCCURRENCE to get unique rows
    const occurrenceMap = new Map<number, any>();
    
    data.jsonObject.forEach((row: any) => {
      const occurrence = row.OCCURRENCE;
      if (!occurrenceMap.has(occurrence)) {
        occurrenceMap.set(occurrence, {
          OCCURRENCE: occurrence,
          DATA_ATTRIB_A: row.DATA_ATTRIB_A || '',
          DATA_ATTRIB_B: row.DATA_ATTRIB_B || 0
        });
      } else {
        // Update existing entry with any missing values
        const existing = occurrenceMap.get(occurrence);
        if (!existing.DATA_ATTRIB_A && row.DATA_ATTRIB_A) {
          existing.DATA_ATTRIB_A = row.DATA_ATTRIB_A;
        }
        if (!existing.DATA_ATTRIB_B && row.DATA_ATTRIB_B) {
          existing.DATA_ATTRIB_B = row.DATA_ATTRIB_B;
        }
      }
    });

    const subTable20c = Array.from(occurrenceMap.values());

    // Set subtableRows directly for template binding
    this.subtableRows = subTable20c;

    if (this.line20CNumber >= 0 && this.formRows[this.line20CNumber]) {
      this.formRows[this.line20CNumber].JSON_DATA = subTable20c;
    } else {
      console.error('PROBLEM: line20CNumber=' + this.line20CNumber + ', formRows[line20CNumber]=' + this.formRows[this.line20CNumber]);
    }

    if (subTable20c.length === 0) {
      this.subtableString = 'No Records.';
    } else {
      this.subtableString = '';
    }

    this.formRowsOriginal = safeJsonInput<any>(JSON.stringify(this.formRows), null, 'f1065-sch-k.parse3');
    this.subtableRowsOriginal = safeJsonInput<any>(JSON.stringify(this.subtableRows), null, 'f1065-sch-k.parse4');
    
    this.cdr.detectChanges();
  }

  // Process subtable data received directly from AngularJS (bypasses Angular HTTP call)
  private processSubtableDataFromAngularJS(jsonArray: any[]): void {
    
    if (!jsonArray || jsonArray.length === 0) {
      this.subtableString = 'No Records.';
      return;
    }

    // Group by OCCURRENCE to get unique rows
    const occurrenceMap = new Map<number, any>();
    
    jsonArray.forEach((row: any) => {
      const occurrence = row.OCCURRENCE;
      if (!occurrenceMap.has(occurrence)) {
        occurrenceMap.set(occurrence, {
          OCCURRENCE: occurrence,
          DATA_ATTRIB_A: row.DATA_ATTRIB_A || '',
          DATA_ATTRIB_B: row.DATA_ATTRIB_B || 0
        });
      } else {
        const existing = occurrenceMap.get(occurrence);
        if (!existing.DATA_ATTRIB_A && row.DATA_ATTRIB_A) {
          existing.DATA_ATTRIB_A = row.DATA_ATTRIB_A;
        }
        if (!existing.DATA_ATTRIB_B && row.DATA_ATTRIB_B) {
          existing.DATA_ATTRIB_B = row.DATA_ATTRIB_B;
        }
      }
    });

    const subTable20c = Array.from(occurrenceMap.values());
    // Set subtableRows directly for template binding
    this.subtableRows = subTable20c;

    if (this.line20CNumber >= 0 && this.formRows[this.line20CNumber]) {
      this.formRows[this.line20CNumber].JSON_DATA = subTable20c;
    }

    if (subTable20c.length === 0) {
      this.subtableString = 'No Records.';
    } else {
      this.subtableString = '';
    }

    if (this.formRows.length > 0) {
      this.formRowsOriginal = safeJsonInput<any>(JSON.stringify(this.formRows), null, 'f1065-sch-k.parse5');
      this.subtableRowsOriginal = safeJsonInput<any>(JSON.stringify(this.subtableRows), null, 'f1065-sch-k.parse6');
    }
    this.cdr.detectChanges();
  }

  // ========== HELPER METHODS ==========
  getObjectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  // Check if row should be shown based on tax year
  shouldShowRow(num: string): boolean {
    if (this.taxYear < 2023) {
      return ['6a', '6b', '6c', '9a', '9b', '9c', '13c', '13d', '14a', '14b', '14c',
              '15d', '15e', '15f', '16p', '16', '20a', '20b', '20c', '21'].includes(num);
    } else if (this.taxYear < 2025) {
      return ['6a', '6b', '6c', '9a', '9b', '9c', '13d(1)', '13e(1)', '14a', '14b', '14c',
              '15d', '15e', '15f', '16p', '16', '20a', '20b', '20c', '21'].includes(num);
    } else {
      return ['6a', '6b', '6c', '9a', '9b', '9c', '13d(1)', '13e(1)', '14a', '14b', '14c',
              '15d', '15e', '15f', '16p', '16a', '16b', '20a', '20b', '20c', '21'].includes(num);
    }
  }

  // Check if row should have empty value cell
  isEmptyValueCell(num: string): boolean {
    if (this.taxYear < 2023) {
      return ['13c', '13d', '15d', '15e', '15f', '16p', '16', '20c'].includes(num);
    } else if (this.taxYear < 2025) {
      return ['13c', '13d', '13d(1)', '13e(1)', '15d', '15e', '15f', '16p', '16', '20c'].includes(num);
    } else {
      return ['13c', '13d', '13d(1)', '13e(1)', '15d', '15e', '15f', '16p', '16a', '16b', '20c'].includes(num);
    }
  }

  unselectOther(index: number, type: string): void {
    if (type === 'Paid') {
      this.formRows[index + 1].value1 = false;
    } else {
      this.formRows[index - 1].value1 = false;
    }
  }

  setValue(index: number, currentValue: string): void {
    this.formRows[index].value1 = currentValue === '1' ? '0' : '1';
  }

  // Helper to get subtable data for 20c
  getSubtableData(): any[] {
    const data = this.line20CNumber >= 0 && this.formRows[this.line20CNumber]?.JSON_DATA 
      ? this.formRows[this.line20CNumber].JSON_DATA 
      : [];
    return data;
  }

  addRow(): void {
    const newRow = {
      OCCURRENCE: this.subtableRows.length + 1,
      DATA_ATTRIB_A: '',
      DATA_ATTRIB_B: 0
    };

    this.subtableRows.push(newRow);
    
    // Also update formRows for save
    if (this.line20CNumber >= 0 && this.formRows[this.line20CNumber]) {
      this.formRows[this.line20CNumber].JSON_DATA = this.subtableRows;
    }
    
    this.cdr.detectChanges();
  }

  removeSubtableRow(index: number): void {
    this.subtableRows.splice(index, 1);
    
    // Also update formRows for save
    if (this.line20CNumber >= 0 && this.formRows[this.line20CNumber]) {
      this.formRows[this.line20CNumber].JSON_DATA = this.subtableRows;
    }
    
    if (this.subtableRows.length === 0) {
      this.subtableString = 'No Records.';
    }
    
    this.cdr.detectChanges();
  }

  // ========== VALIDATION ==========
  validateValues(): boolean {
    let hasError = false;

    // Validate 6b >= 0
    if (this.formRows[9]?.value1 < 0) {
      hasError = true;
      this._snackBar.open("Value of line 6b can't be less than 0", 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
    }

    // Validate 6c >= 0
    if (this.formRows[10]?.value1 < 0) {
      hasError = true;
      this._snackBar.open("Value of line 6c can't be less than 0", 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
    }

    // Validate 6a >= 6b + 6c
    if (this.formRows[8]?.value1 !== '' &&
        this.formRows[9]?.value1 !== '' &&
        this.formRows[10]?.value1 !== '') {
      const val6a = parseInt(this.formRows[8].value1, 10) || 0;
      const val6b = parseInt(this.formRows[9].value1, 10) || 0;
      const val6c = parseInt(this.formRows[10].value1, 10) || 0;

      if (val6a < (val6b + val6c)) {
        hasError = true;
        this._snackBar.open('Amount on 6a should be greater than or equal to Sum of 6b and 6c', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    }

    // Validate 9a >= 9b + 9c (absolute values)
    if (this.formRows[13]?.value1 !== '' &&
        this.formRows[14]?.value1 !== '' &&
        this.formRows[15]?.value1 !== '') {
      const val9a = Math.abs(parseInt(this.formRows[13].value1, 10) || 0);
      const val9b = Math.abs(parseInt(this.formRows[14].value1, 10) || 0);
      const val9c = Math.abs(parseInt(this.formRows[15].value1, 10) || 0);

      if (val9a < (val9b + val9c)) {
        hasError = true;
        this._snackBar.open('Amount on 9a should be greater than or equal to Sum of 9b and 9c', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    }

    this.canSave = !hasError;
    return !hasError;
  }

  // ========== SAVE ==========
  save(): void {
    if (!this.validateValues()) return;

    this.isSaving = true;
    const formData = this.prepareSaveData();

    this.f1065SchKService.saveScheduleKForm(
      this.baseUrl,
      formData,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.processName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // CRITICAL: Emit 'refresh' to trigger grid refresh and modal close (SuperPrompt V9)
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Save error:', err);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    });
  }

  private prepareSaveData(): any[] {
    const tempArray: any[] = [];
    const excludedNums = ['6a', '9a', '13c', '13d', '16p'];

    for (let i = 0; i < this.formRows.length; i++) {
      const row = this.formRows[i];

      if (!excludedNums.includes(row.num)) {
        const formRowDataA: any = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_a,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKeyA,
          Attribute_Name: row.attrib_name_a,
          Attribute_Value: '' + row.value1,
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: ''
        };

        // Handle line 20c subtable
        if (i === this.line20CNumber) {
          formRowDataA.JSON_DATA = [];
          const attributes = ['A', 'B'];
          let index = 1;

          this.subtableRows.forEach((subRow: any) => {
            let hasData = false;
            const tempDataArray: any[] = [];

            attributes.forEach(attr => {
              const tempObj = {
                OCCURRENCE: index,
                ATTRIB_NAME: 'DATA_ATTRIB_' + attr,
                ATTRIB_VALUE: subRow['DATA_ATTRIB_' + attr]
              };
              if (tempObj.ATTRIB_VALUE !== undefined && tempObj.ATTRIB_VALUE !== '') {
                hasData = true;
              }
              tempDataArray.push(tempObj);
            });

            index++;
            if (hasData) {
              tempDataArray.forEach(col => formRowDataA.JSON_DATA.push(col));
            }
          });
        }

        tempArray.push(formRowDataA);

        // For lines 16a/16b, also add B attribute (ISSUE_ID)
        if (row.num === '16a' || row.num === '16b') {
          const formRowDataB: any = {
            tax_year: this.rowData.TAX_YEAR,
            trans_type_key: row.saveParams[1],
            line_order: row.line_order,
            attrib_order: row.attrib_order_b,
            line_description: row.description,
            combination_key: this.rowData.COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: row.saveParams[2],
            line_no: row.num,
            occurence: row.saveParams[7],
            line_attrib_key: row.lineAttrKeyB,
            Attribute_Name: row.attrib_name_b,
            Attribute_Value: row.value2 || '',
            trans_dtls_key: row.saveParams[3],
            trans_status: row.saveParams[6],
            is_dirty: ''
          };
          tempArray.push(formRowDataB);
        }

        // For line 20c, also add B attribute
        if (i === this.line20CNumber) {
          const formRowDataB: any = {
            tax_year: this.rowData.TAX_YEAR,
            trans_type_key: row.saveParams[1],
            line_order: row.line_order,
            attrib_order: row.attrib_order_b,
            line_description: row.description,
            combination_key: this.rowData.COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: row.saveParams[2],
            line_no: row.num,
            occurence: row.saveParams[7],
            line_attrib_key: row.lineAttrKeyB,
            Attribute_Name: row.attrib_name_b,
            Attribute_Value: row.value2,
            trans_dtls_key: row.saveParams[3],
            trans_status: row.saveParams[6],
            is_dirty: ''
          };
          tempArray.push(formRowDataB);
        }
      }
    }

    // Copy JSON_DATA from 20c to 20c1
    let line20C_JSON_DATA: any[] = [];
    tempArray.forEach(row => {
      if (row.line_no === '20c' && row.JSON_DATA?.length > 0) {
        line20C_JSON_DATA = row.JSON_DATA;
      }
      if (row.line_no === '20c1') {
        row.JSON_DATA = line20C_JSON_DATA;
      }
    });

    return [{
      addrows: [],
      editrows: [tempArray],
      removerows: []
    }];
  }

  reset(): void {
    this.formRows = safeJsonInput<any>(JSON.stringify(this.formRowsOriginal), null, 'f1065-sch-k.parse7');
    this.subtableRows = safeJsonInput<any>(JSON.stringify(this.subtableRowsOriginal), null, 'f1065-sch-k.parse8');
    
    // Update subtableString based on restored data
    if (this.subtableRows.length === 0) {
      this.subtableString = 'No Records.';
    } else {
      this.subtableString = '';
    }
    
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
